import AppModal from '@/components/AppModal/AppModal';
import { config } from '@/config';
import { languageList, languageToLocale } from '@/constants/language';
import { openToast } from '@/helpers';
import { useCreateAppearanceTextMutation, useGetWidgetAppearanceTextQuery } from '@/redux/query';
import { widgetTextPaginationSettingSelector } from '@/redux/slice/widgetText.slice';
import { ITextTable } from '@/types/components/translation';
import { BlockStack, Select } from '@shopify/polaris';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

interface Props {
  open: boolean;
  onCancel: () => void;
  fullWidgetAppearanceText?: ITextTable[];
}

export const AddLanguageModal = ({ open, onCancel, fullWidgetAppearanceText }: Props) => {
  const { t } = useTranslation('translation');
  const [createAppearanceText] = useCreateAppearanceTextMutation();
  const widgetTextPaginationSetting = useSelector(widgetTextPaginationSettingSelector);
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const { refetch: refetchGetWidgetAppearanceText } = useGetWidgetAppearanceTextQuery({
    shop: config.shop,
    offset: (widgetTextPaginationSetting.page - 1) * widgetTextPaginationSetting.perPage,
    limit: widgetTextPaginationSetting.perPage,
  });
  const { refetch: refetchFullGetWidgetAppearanceText } = useGetWidgetAppearanceTextQuery({
    shop: config.shop,
  });

  const dispatch = useDispatch();

  const handleSelectChange = useCallback((value: string) => setSelectedLanguage(value), []);

  const languageCodeExist = useMemo(
    () => fullWidgetAppearanceText?.filter((item) => !item.defaultLanguage).map((item) => item.code),
    [fullWidgetAppearanceText],
  );

  const calendarLocaleCode = useMemo(() => languageToLocale[selectedLanguage] || 'en-US', [selectedLanguage]);

  const handleCreateAppearanceText = () => {
    setIsAdding(true);
    createAppearanceText({
      shop: config.shop,
      code: selectedLanguage,
      name: languageList.find((item) => item.value === selectedLanguage)?.label || '',
      calendarLocale: calendarLocaleCode,
    })
      .unwrap()
      .then(() => refetchGetWidgetAppearanceText())
      .then(() => refetchFullGetWidgetAppearanceText())
      .then(() => openToast(dispatch, 'Add language successfully'))
      .then(() => setIsAdding(false))
      .then(() => onCancel());
  };

  useEffect(() => {
    setSelectedLanguage(languageList.filter((item) => !languageCodeExist?.includes(item.value))[0].value);
  }, [languageCodeExist]);

  return (
    <AppModal
      open={open}
      onCancel={onCancel}
      onSubmit={handleCreateAppearanceText}
      cancelText={t('manageLanguages.addLanguageModal.cancelText')}
      submitText={t('manageLanguages.addLanguageModal.submitText')}
      title={t('manageLanguages.addLanguageModal.title')}
      loading={isAdding}
    >
      <BlockStack gap={'300'}>
        <Select
          label={t('manageLanguages.addLanguageModal.selectLanguageLabel')}
          options={languageList.filter((item) => !languageCodeExist?.includes(item.value))}
          onChange={handleSelectChange}
          value={selectedLanguage}
        />
      </BlockStack>
    </AppModal>
  );
};
